import { useNavigate, useParams, useLocation } from "react-router-dom";

import toast from "react-hot-toast";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  axiosWithToast,
  dateToString,
  HtmlTextFormatter,
  ItemStatus,
} from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";

import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  useUserHasAccess,
  useWorkOrderPermissions,
  useProjectWorkOrdersAccess,
  useCompanyHasModule,
} from "../../../routes/roleOnly";
import { useSelector, useDispatch } from "react-redux";

import { PMNotice } from "../../../components/utils/notifications";
import {
  CheckBadgeIcon,
  TrashIcon,
  ClockIcon,
  CubeIcon,
  PencilIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  PrinterIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../../components/utils/PMDialog";
import { PMButton } from "../../../components/utils/PMButton";
import PMSwitch from "../../../components/utils/PMSwitch";
import ProConfirmation from "../../../components/utils/ProConfirmation";
import {
  createWorkOrder,
  deleteWorkOrder,
  fetchWorkOrder,
  updateWorkOrder,
} from "../../../api/work-order";
import { TinyDocumentSection } from "../../../components/dashboard/documents/tinyDocumentSection";
import OrderItems from "../../../components/dashboard/orders/order-items";

import { getHoursAsync } from "../../../slices/hour";

import { NotesAdvancedSection } from "../../../components/dashboard/notes/notesAdvancedSection";
import { WorkOrderPrintDialog } from "../../../components/dashboard/workOrders/work-order-print-dialog";
import { WorkOrderEventsSection } from "../../../components/dashboard/workOrders/work-order-events-section";
import { WorkOrderAccessesSection } from "../../../components/dashboard/workOrders/WorkOrderAccessesSection";
import WorkOrderFinishDialog from "../../../components/dashboard/workOrders/work-order-finish-dialog";
import { WorkOrderBillingReadyDialog } from "../../../components/dashboard/workOrders/work-order-billing-ready-dialog";
import { WorkOrderAdminMenu } from "../../../components/dashboard/workOrders/work-order-admin-menu";
import { WorkOrderAdminSetStatusDialog } from "../../../components/dashboard/workOrders/work-order-admin-set-status-dialog";
import { WorkOrderCompletionStatusCard } from "../../../components/dashboard/workOrders/work-order-completion-status-card";
import { TimeRegistrationSection } from "../../../components/dashboard/hours/time-registration-section";
import { getChecklistStatus } from "../../../components/dashboard/service/reports/utils";
import { ReportSectionCard } from "../../../components/dashboard/service/reports/report-section-card";

export const ReadWorkOrder = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();

  const [isLoading, setIsLoading] = useState(true);
  const isMananger = useUserHasAccess({ role: "projects_work-orders", action: "manage" });
  // const isMananger = false;
  const currentUser = useSelector((state) => state.session.currentUser);
  const dateToday = new Date().toISOString().substring(0, 10);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [workOrderPrintDialogOpen, setWorkOrderPrintDialogOpen] =
    useState(false);
  const [workOrderFinishDialogOpen, setWorkOrderFinishDialogOpen] =
    useState(false);
  const [billingReadyDialogOpen, setBillingReadyDialogOpen] = useState(false);
  const [adminSetStatusDialogOpen, setAdminSetStatusDialogOpen] =
    useState(false);

  const [customer, setCustomer] = useState(
    location?.state?.customer || props?.customer
  );
  const [project, setProject] = useState(
    location?.state?.project || props?.project
  );
  const [delivery_address, setDeliveryAddress] = useState(
    location?.state?.delivery_address || props?.delivery_address
  );

  const navigate = useNavigate(); // Assuming you have a navigate function from react-router-dom

  const [work_order, setWorkOrder] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Use the new permissions hook
  const permissions = useWorkOrderPermissions(work_order);
  const workOrdersModuleAccess = useProjectWorkOrdersAccess(work_order?.project);
  const qualityChecklistsModule = useCompanyHasModule("quality_checklists");
  const canEditWorkOrderUi =
    Boolean(permissions?.canEdit) && workOrdersModuleAccess.canEdit;
  const canDeleteWorkOrderInModule = workOrdersModuleAccess.canDelete;

  const [initialValues, setInitialValues] = useState({
    id: null,
    title: "",
    number: "",
    order_date: dateToday,
    due_date: dateToday,
    project_id: project?.id,
    description: "",
    is_urgent: false,
    participants: [],
    submit: null,
  });

  // Hours fetching logic (similar to report-finish.js)
  const dispatch = useDispatch();
  const hoursState = useSelector((store) => store.hours);

  const [timeRegistrationDialogSignal, setTimeRegistrationDialogSignal] =
    useState(0);

  const [hoursController, setHoursController] = useState({
    query: "",
    page: 1,
    per_page: 100, // Get more hours to ensure we have all
    sortBy: "created_at",
    sort: "desc",
    filters: [],
    view: null,
    fields: "time,effective_hourly_rate",
  });

  // Calculate total registered hours from hours data
  const totalRegisteredHours =
    hoursState?.data?.reduce((sum, hour) => {
      return sum + (parseFloat(hour.time) || 0);
    }, 0) || 0;

  // Calculate total labor costs from hours data using actual hour rates
  const totalLaborCosts =
    hoursState?.data?.reduce((sum, hour) => {
      const time = parseFloat(hour.time) || 0;
      const hourRate = parseFloat(hour.effective_hourly_rate) || 500; // Use effective rate from API
      return sum + time * hourRate;
    }, 0) || 0;

  // Calculate total material costs from work order items
  const totalMaterialCosts =
    work_order?.items?.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const purchasePrice = parseFloat(item.purchase_price) || 0;
      return sum + quantity * purchasePrice;
    }, 0) || 0;

  // Use work_order.budgeted_hours for the budget
  const budgetedHours = work_order?.budgeted_hours || 0;

  // Calculate progress percentage
  const progressPercentage =
    budgetedHours > 0
      ? Math.min((totalRegisteredHours / budgetedHours) * 100, 100)
      : 0;

  // Fetch hours when work order is loaded
  useEffect(() => {
    if (work_order?.id) {
      const filters = [{ name: "WORK_ORDER", value: work_order.id }];
      setHoursController((prev) => ({
        ...prev,
        filters: filters,
      }));
      dispatch(getHoursAsync({ ...hoursController, filters }));
    }
  }, [work_order?.id]);

  useEffect(() => {
    // if ID is set, fetch the work_order
    setTimeout(() => {
      if (id) {
        setIsLoading(true);
        getWorkOrder(id);
      } else {
        setIsLoading(false);
      }
    }, 500);
  }, [id]);

  // Update state when location.state changes
  useEffect(() => {
    if (location?.state) {
      setCustomer(location.state.customer || customer);
      setProject(location.state.project || project);
      setDeliveryAddress(location.state.delivery_address || delivery_address);
    }
  }, [location?.state]);

  useEffect(() => {
    if (work_order) {
      setInitialValues({
        id: work_order.id,
        title: work_order?.title,
        number: work_order?.number,
        order_date: work_order?.order_date || dateToday,
        due_date: work_order?.due_date || dateToday,
        project_id: work_order?.project_id || project?.id,
        customer_id: work_order?.customer_id || customer?.id,
        delivery_address_id:
          work_order?.delivery_address_id || delivery_address?.id,
        description: work_order?.description || "",
        participants: work_order?.participants || [],
        submit: null,
      });
    } else {
      setInitialValues({
        id: null,
        title: "",
        number: "",
        order_date: dateToday,
        due_date: dateToday,
        project_id: project?.id,
        customer_id: customer?.id,
        delivery_address_id: delivery_address?.id,
        description: work_order?.description || "",
        is_urgent: false,
        participants: [],
        submit: null,
      });
    }
  }, [
    work_order,
    isMananger,
    currentUser.id,
    dateToday,
    project?.id,
    customer?.id,
    delivery_address?.id,
  ]);

  const getWorkOrder = async (id) => {
    setIsLoading(true);
    setAccessDenied(false);

    try {
      const res = await fetchWorkOrder(id, { fields: "*" });
      const wo = res?.data?.data ?? res?.data;
      setWorkOrder(wo);
    } catch (error) {
      console.error("Error fetching work order:", error);

      if (error.response && error.response.status === 403) {
        setAccessDenied(true);
        toast.error("Du har ikke tilgang til denne arbeidsordren");
      } else {
        toast.error("Something went wrong when trying to load the work order");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createOrUpdate = async (data) => {
    try {
      if (work_order?.id) {
        const result = await updateWorkOrder(data, { params: { fields: "*" } });
        return result;
      } else {
        const result = await createWorkOrder(data, { fields: "*" });
        return result;
      }
    } catch (error) {
      console.error("Error in createOrUpdate:", error);
      throw error;
    }
  };

  // get userType form searchParams
  const searchParams = new URLSearchParams(location.search);
  const _userType = searchParams.get("userType") || "";

  const [errors, setErrors] = useState([]);

  // remove errors after 6 seconds
  useEffect(() => {
    if (errors && errors.length > 0) {
      setTimeout(() => {
        setErrors([]);
      }, 20000);
    }
  }, [errors]);

  const [approveDialogOpen, setApproveDialogOpen] = useState(false);

  const [itemConsumptionDialogOpen, setItemConsumptionDialogOpen] =
    useState(false);
  const [costEstimationOpen, setCostEstimationOpen] = useState(false);

  const handleParticipantsChange = useCallback((next) => {
    setWorkOrder((w) => (w ? { ...w, participants: next } : w));
  }, []);

  const handleEventsMutated = useCallback(({ action, event, id }) => {
    setWorkOrder((w) => {
      if (!w) return w;
      const evs = w.events || [];
      const promoteDraftToOngoing =
        (action === "created" || action === "updated") &&
        event &&
        event.status !== "cancelled" &&
        w.status === "draft";
      const statusPatch = promoteDraftToOngoing ? { status: "ongoing" } : {};

      if (action === "deleted") {
        return { ...w, events: evs.filter((e) => e.id !== id) };
      }
      if (action === "created") {
        return { ...w, ...statusPatch, events: [...evs, event] };
      }
      if (action === "updated" && event) {
        return {
          ...w,
          ...statusPatch,
          events: evs.map((e) => (e.id === event.id ? event : e)),
        };
      }
      return w;
    });
  }, []);

  // Memoize NotesAdvancedSection props with stable dependency
  const notesSectionProps = useMemo(
    () => ({
      module: "work_order",
      module_param: work_order?.id,
      enableFileUpload: true,
      enableTemplates: true,
      enableTags: false,
      enableMentions: false,
      mobileFullscreenComposer: true,
      title: "Arbeidsnotater",
      subtitle:
        "Før en kort arbeidsrapport med beskrivelse, bilder og viktige detaljer fra jobben.",
      placeholder:
        "Beskriv utført arbeid...",
      submitLabel: "Lagre rapport",
    }),
    [work_order?.id]
  );

  const canMarkReadyForInvoicing = useMemo(() => {
    if (!work_order?.id) return false;
    if (work_order?.order?.id) return false;
    if (!work_order.is_completed) return false;
    if (work_order.sales_income_included_in_project) return false;
    if (["cancelled", "invoiced", "draft", "ongoing"].includes(work_order.status))
      return false;
    const role = work_order.role_for_current_user;
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    if (!roleNames.includes("projects_work-orders_edit")) return false;
    return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
  }, [work_order, currentUser?.roles]);

  const canOpenLinkedSalesOrder = useMemo(() => {
    if (!work_order?.order?.id) return false;
    const roleNames = (currentUser?.roles || []).map((r) => r.name);
    if (roleNames.includes("admin")) return true;
    if (!roleNames.includes("projects_work-orders_edit")) return false;
    const role = work_order.role_for_current_user;
    return ["RESPONSIBLE", "RESPONSIBLE_EXECUTOR"].includes(role);
  }, [work_order, currentUser?.roles]);

  const canAdminSetStatus = useMemo(() => {
    if (!work_order?.id) return false;
    if (!workOrdersModuleAccess.canRead) return false;
    return (currentUser?.roles || []).some((r) => r.name === "admin");
  }, [work_order?.id, workOrdersModuleAccess.canRead, currentUser?.roles]);

  const hasWorkOrderAdminAccess = useMemo(
    () => isMananger || workOrdersModuleAccess.canEdit,
    [isMananger, workOrdersModuleAccess.canEdit]
  );

  const showWorkOrderAdminMenu = useMemo(() => {
    if (!work_order?.id) return false;
    return (
      hasWorkOrderAdminAccess ||
      canMarkReadyForInvoicing ||
      canOpenLinkedSalesOrder ||
      canAdminSetStatus ||
      (isMananger && !work_order?.accepted)
    );
  }, [
    work_order?.id,
    work_order?.accepted,
    hasWorkOrderAdminAccess,
    canMarkReadyForInvoicing,
    canOpenLinkedSalesOrder,
    canAdminSetStatus,
    isMananger,
  ]);

  const canShowWorkOrderApproveInMenu = useMemo(
    () => Boolean(isMananger && work_order?.id && !work_order?.accepted),
    [isMananger, work_order?.id, work_order?.accepted]
  );

  /** Fullført AO: alle med lesetilgang kan åpne dialog. Ellers: samme vilkår som «Ferdigstill» i header. */
  const canUseFinishDialog = useMemo(() => {
    if (!work_order?.id) return false;
    if (work_order.is_completed) return true;
    if (!["draft", "ongoing", "rejected"].includes(work_order.status))
      return false;
    return Boolean(
      canEditWorkOrderUi || permissions?.canFinishWorkOrder
    );
  }, [
    work_order?.id,
    work_order?.is_completed,
    work_order?.status,
    canEditWorkOrderUi,
    permissions?.canFinishWorkOrder,
  ]);

  const headerActions = useMemo(() => {
    const canOpenEditForFieldStaff =
      Boolean(workOrdersModuleAccess.canRead) &&
      (Boolean(permissions?.canExecute) ||
        Boolean(permissions?.canAddNote) ||
        Boolean(permissions?.canWorkNotes));

    return [
      {
        component: null,
        label: "Rediger",
        description: "",
        path: `/dashboard/work_orders/edit/${work_order?.id}`,
        action: null,
        icon: PencilIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden:
          (!canEditWorkOrderUi && !canOpenEditForFieldStaff) || !work_order?.id,
        menu: false,
      },
      {
        component: null,
        label: "Skriv ut",
        description: "",
        path: null,
        action: () => setWorkOrderPrintDialogOpen(true),
        icon: PrinterIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !work_order?.id,
        menu: false,
      },
      {
        component: null,
        label: "Ferdigstill",
        description: "",
        path: null,
        action: () => setWorkOrderFinishDialogOpen(true),
        icon: CheckBadgeIcon,
        textColor: "text-emerald-700",
        bgColor: "bg-emerald-100",
        hidden:
          (!canEditWorkOrderUi && !permissions?.canFinishWorkOrder) ||
          work_order?.is_completed ||
          !["draft", "ongoing", "rejected"].includes(work_order?.status),
        menu: false,
      },
      {
        component: null,
        label: "Slett",
        description: "",
        path: null,
        action: () => setDeleteConfirmationOpen(true),
        icon: TrashIcon,
        textColor: "text-teal-700",
        bgColor: "bg-white",
        hidden: !(canDeleteWorkOrderInModule || isMananger),
        menu: true,
      },
    ];
  }, [
    work_order?.id,
    work_order?.status,
    work_order?.is_completed,
    isMananger,
    canEditWorkOrderUi,
    permissions?.canFinishWorkOrder,
    workOrdersModuleAccess.canRead,
    permissions?.canExecute,
    permissions?.canAddNote,
    permissions?.canWorkNotes,
    canDeleteWorkOrderInModule,
    setDeleteConfirmationOpen,
    setWorkOrderPrintDialogOpen,
    setWorkOrderFinishDialogOpen,
    t,
  ]);

  const headerMeta = useMemo(
    () => [
      {
        label: `Opprettet ${dateToString(work_order?.order_date)}`,
        hidden: !work_order?.order_date,
      },
      {
        label: `Frist ${dateToString(work_order?.due_date)}`,
        hidden: !work_order?.due_date,
      },
      {
        component: <ItemStatus item={work_order} />,
        hidden: false,
      },
      {
        label: `Godkjent av ${work_order?.approved_by?.displayTitle}`,
        hidden: !work_order?.accepted,
      },
    ],
    [
      work_order,
      work_order?.accepted,
      work_order?.approved_by?.displayTitle,
      work_order?.order_date,
      work_order?.due_date,
    ]
  );

  const headerSummaryItems = useMemo(
    () => [
      {
        label: "Prosjekt",
        value: work_order?.project?.displayTitle || "-",
        path: work_order?.project?.id
          ? `/dashboard/projects/${work_order.project.id}`
          : null,
      },
      {
        label: t("work_orders.project_activity.linked_label"),
        value:
          work_order?.project_activity?.display_title ||
          work_order?.project_activity?.title ||
          "-",
      },
      {
        label: "Kunde",
        value: work_order?.customer?.displayTitle || "-",
        path: work_order?.customer?.id
          ? `/dashboard/customers/${work_order.customer.id}`
          : null,
      },
      {
        label: "Budsjetterte timer",
        value: `${work_order?.budgeted_hours || 0} timer`,
      },
    ],
    [
      t,
      work_order?.project?.displayTitle,
      work_order?.project?.id,
      work_order?.project_activity?.display_title,
      work_order?.project_activity?.title,
      work_order?.customer?.displayTitle,
      work_order?.customer?.id,
      work_order?.budgeted_hours,
    ]
  );

  const headerSummaryFooter = useMemo(() => {
    if (!work_order?.description) return null;

    return (
      <>
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Beskrivelse
        </h4>
        <div className="prose prose-sm mt-3 max-w-none text-gray-700">
          <HtmlTextFormatter string={work_order.description} />
        </div>
      </>
    );
  }, [work_order?.description]);

  const handleDeleteWorkOrder = () => {
    if (!work_order?.id) return;
    setDeleteConfirmationOpen(false);
    setIsLoading(true);
    deleteWorkOrder(work_order.id)
      .then(() => {
        setIsLoading(false);
        toast.success(t("work_orders.delete_success"));
        navigate("/dashboard/work_orders");
      })
      .catch((err) => {
        setIsLoading(false);
        const raw = err?.response?.data?.errors;
        const msg = Array.isArray(raw)
          ? raw[0]
          : typeof raw === "string"
            ? raw
            : null;
        toast.error(msg || t("work_orders.delete_failed"));
      });
  };

  const Content = useCallback(() => {
    return (
      <div className="mx-auto mt-8 max-w-7xl px-4 sm:px-6 pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:grid lg:grid-cols-[1fr_18rem] lg:gap-6 lg:pb-5">
        <div className="min-w-0 space-y-6">
          <div className="divide-y divide-gray-200 overflow-hidden rounded-lg sm:gap-px sm:divide-y-0">
            {/* Rejection Notification Section */}
            {work_order?.is_rejected && (
              <div className="mt-5 mb-5">
                <PMNotice
                  severity="error"
                  title="Arbeidsordre ikke godkjent"
                  description={
                    <div className="space-y-2">
                      {work_order?.approval_note && (
                        <div>
                          <span className="font-medium">Begrunnelse:</span>{" "}
                          {work_order.approval_note}
                        </div>
                      )}
                      <div className="text-sm">
                        <span className="font-medium">Behandlet av:</span>{" "}
                        {work_order?.approval_by?.displayTitle || "Ukjent"}
                        {work_order?.updated_at && (
                          <>
                            {" "}
                            <span className="font-medium">den</span>{" "}
                            {dateToString(work_order.updated_at)}
                          </>
                        )}
                      </div>
                    </div>
                  }
                />
              </div>
            )}

            {work_order?.id ? (
              <div className="mb-5">
                <WorkOrderCompletionStatusCard
                  workOrder={work_order}
                  canOpenFinish={canUseFinishDialog}
                  onOpenFinish={() => setWorkOrderFinishDialogOpen(true)}
                />
              </div>
            ) : null}

            {qualityChecklistsModule &&
            (work_order?.uses_checklist || work_order?.checklist_id) ? (
              <div className="mb-5">
                {(() => {
                  const checklistData = work_order?.checklist;
                  const checklistStatus = getChecklistStatus(checklistData);
                  return (
                    <ReportSectionCard
                      title={t("work_orders.checklist.card_title")}
                      description={
                        checklistData?.id
                          ? t("work_orders.checklist.card_desc_with_checklist")
                          : t("work_orders.checklist.card_desc_no_checklist")
                      }
                      icon={ClipboardDocumentIcon}
                      count={checklistStatus.completedCount}
                      total={checklistStatus.totalCount}
                      unit={t("work_orders.checklist.unit")}
                      statusText={checklistStatus.statusText}
                      isCompleted={checklistStatus.isCompleted}
                      isRequired={checklistStatus.isRequired}
                      showProgress={Boolean(checklistData?.id)}
                      progressValue={checklistStatus.completedCount}
                      progressMax={checklistStatus.totalCount}
                      canEdit={canEditWorkOrderUi}
                      to={`/dashboard/work_orders/${work_order.id}/checklist`}
                    />
                  );
                })()}
              </div>
            ) : null}

            {work_order?.sales_income_included_in_project ? (
              <div className="mb-5">
                <PMNotice
                  severity="info"
                  title={t(
                    "work_orders.billing_ready.sales_income_in_project_notice_title"
                  )}
                  description={t(
                    "work_orders.billing_ready.sales_income_in_project_notice"
                  )}
                />
              </div>
            ) : null}

            {/* Progress Bar Section */}
            {budgetedHours > 0 && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">
                      Timer Progress
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Viser timer registrert direkte pÃ¥ denne arbeidsordren
                      sammenlignet med budsjetterte timer
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Timer brukt
                      </span>
                      <span className="text-sm text-gray-500">
                        {totalRegisteredHours.toFixed(1)} / {budgetedHours}{" "}
                        timer
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full transition-all duration-300 ${
                          progressPercentage >= 100
                            ? "bg-red-500"
                            : progressPercentage >= 80
                            ? "bg-yellow-500"
                            : "bg-blue-500"
                        }`}
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>0h</span>
                      <span>{budgetedHours}h</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {work_order?.id && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md mb-5">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div className="grid grid-cols-6 gap-6">
                    <div className="col-span-6">
                      <TinyDocumentSection
                        module="work_order_description"
                        moduleParam={work_order?.id}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Hours + item consumption: cards on lg+; mobile uses fixed bottom nav */}
            <div className="mt-5 lg:grid lg:grid-cols-2 lg:gap-6">
              <TimeRegistrationSection
                className="hidden lg:block"
                module="work_order"
                moduleId={work_order?.id}
                project={work_order?.project}
                workOrder={work_order}
                onRefresh={() => {
                  if (work_order?.id) {
                    const filters = [{ name: "WORK_ORDER", value: work_order.id }];
                    dispatch(getHoursAsync({ ...hoursController, filters }));
                  }
                }}
                title="Timeregistrering"
                description="Registrer timer for denne arbeidsordren"
                openRegistrationDialogSignal={timeRegistrationDialogSignal}
              />

              <div
                disabled={!permissions.canItemConsumption}
                className={`hidden lg:block shadow sm:overflow-hidden sm:rounded-md cursor-pointer transition-colors duration-200 hover:bg-gray-50 ${
                  !permissions.canItemConsumption
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={
                  permissions.canItemConsumption
                    ? () => navigate(`/dashboard/work_orders/${work_order?.id}/item-consumption`)
                    : null
                }
              >
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <CubeIcon className="h-12 w-12 text-teal-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        Vareforbruk
                      </h3>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Registrer forbruk av materialer og varer
                        </p>
                        <div className="mt-2 flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            <PlusIcon className="h-3 w-3 mr-1" />
                            {work_order?.items?.length || 0} varer registrert
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes: single surface; NotesAdvancedSection uses embedded layout (no nested card). */}
            {work_order?.id && (
              <section className="mt-5 overflow-hidden rounded-lg border border-gray-200 bg-white shadow sm:rounded-md">
                <div className="px-4 py-5 sm:p-6">
                  <NotesAdvancedSection
                    {...notesSectionProps}
                    embedded
                    disabled={!permissions.canAddNote}
                  />
                </div>
              </section>
            )}

            {/* Cost Estimation Section */}
            {permissions.canPriceEstimate && (
              <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                  <div
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setCostEstimationOpen(!costEstimationOpen)}
                  >
                    <div>
                      <h3 className="text-lg font-medium leading-6 text-gray-900">
                        Kostnadsestimering
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Estimert kostnad basert pÃ¥ timer og vareforbruk
                      </p>
                    </div>
                    <div className="flex items-center">
                      {costEstimationOpen ? (
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {costEstimationOpen && (
                    <div className="grid grid-cols-6 gap-6">
                      <div className="col-span-6 sm:col-span-6">
                        <div className="space-y-4">
                          {/* Labor Costs */}
                          <div className="border rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Arbeidskostnader
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Registrerte timer
                                </label>
                                <p className="text-lg font-semibold text-gray-900">
                                  {totalRegisteredHours.toFixed(1)} timer
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Gjennomsnittlig timepris
                                </label>
                                <p className="text-lg font-semibold text-gray-900">
                                  kr{" "}
                                  {totalRegisteredHours > 0
                                    ? (
                                        totalLaborCosts / totalRegisteredHours
                                      ).toFixed(0)
                                    : 500}
                                  /t
                                </p>
                              </div>
                              <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700">
                                  Faktisk arbeidskostnad
                                </label>
                                <p className="text-xl font-bold text-blue-600">
                                  kr {totalLaborCosts.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Material Costs */}
                          <div className="border rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Materialkostnader
                            </h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Registrert materialkostnad
                                </label>
                                <p className="text-lg font-semibold text-gray-900">
                                  kr {totalMaterialCosts.toLocaleString()}
                                </p>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Status
                                </label>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Registrert
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Total Cost */}
                          <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                            <h4 className="font-bold text-gray-900 mb-3">
                              Total faktisk kostnad
                            </h4>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">
                                Sum kostnader:
                              </span>
                              <span className="text-2xl font-bold text-blue-600">
                                kr{" "}
                                {(
                                  totalLaborCosts + totalMaterialCosts
                                ).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              * Basert pÃ¥ faktisk registrerte timer og
                              materialkostnader
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {work_order?.id && (
              <WorkOrderAccessesSection
                workOrderId={work_order.id}
                participants={work_order.participants || []}
                onParticipantsChange={handleParticipantsChange}
                canManage={canEditWorkOrderUi}
                className="mt-5 hidden lg:block"
              />
            )}

            {/* Events Section */}
            {work_order?.id && (
              <WorkOrderEventsSection
                workOrder={work_order}
                events={work_order?.events || []}
                canEdit={canEditWorkOrderUi}
                onEventsMutated={handleEventsMutated}
              />
            )}
          </div>
        </div>

        {showWorkOrderAdminMenu ? (
          <aside className="hidden lg:flex lg:flex-col lg:gap-4">
            <WorkOrderAdminMenu
              workOrder={work_order}
              hasAccess={showWorkOrderAdminMenu}
              onOpenApprove={() => setApproveDialogOpen(true)}
              onOpenBilling={() => setBillingReadyDialogOpen(true)}
              onOpenFinish={() => setWorkOrderFinishDialogOpen(true)}
              onOpenAdminSetStatus={() => setAdminSetStatusDialogOpen(true)}
              canApprove={canShowWorkOrderApproveInMenu}
              canMarkReadyForInvoicing={canMarkReadyForInvoicing}
              canOpenLinkedSalesOrder={canOpenLinkedSalesOrder}
              canEdit={canEditWorkOrderUi}
              canOpenFinish={canUseFinishDialog}
              canAdminSetStatus={canAdminSetStatus}
            />
          </aside>
        ) : null}
      </div>
    );
  }, [
    budgetedHours,
    totalRegisteredHours,
    progressPercentage,
    work_order,
    notesSectionProps,
    costEstimationOpen,
    setItemConsumptionDialogOpen,
    setCostEstimationOpen,
    totalLaborCosts,
    totalMaterialCosts,
    handleParticipantsChange,
    handleEventsMutated,
    canEditWorkOrderUi,
    timeRegistrationDialogSignal,
    showWorkOrderAdminMenu,
    canShowWorkOrderApproveInMenu,
    canMarkReadyForInvoicing,
    canOpenLinkedSalesOrder,
    canAdminSetStatus,
    canUseFinishDialog,
    permissions,
    navigate,
    dispatch,
    hoursController,
    t,
    qualityChecklistsModule,
  ]);

  // Access Denied Component
  const AccessDenied = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Tilgang nektet
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Du har ikke tilgang til denne arbeidsordren
          </p>
        </div>
        <div className="mt-8 space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-500 mb-4">
              Kontakt administrator hvis du mener du skal ha tilgang til denne
              arbeidsordren.
            </p>
            <PMButton
              onClick={() => navigate("/dashboard/work_orders/")}
              className="w-full"
            >
              GÃ¥ tilbake til oversikt
            </PMButton>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {accessDenied ? (
        <AccessDenied />
      ) : (
        <DashboardHeader
          variant="v2"
          meta={headerMeta}
          summaryItems={headerSummaryItems}
          summaryFooter={headerSummaryFooter}
          actions={headerActions}
          title={work_order?.title || "Arbeidsordre"}
          subtitle={
            work_order?.displayTitle
              ? `Arbeidsordre / ${work_order.displayTitle}`
              : "Arbeidsordre"
          }
        >
          <>
            {isLoading ? <ResourceLoading /> : <Content />}
            {work_order?.id && !isLoading && (
              <nav
                className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white/95 shadow-[0_-4px_14px_rgba(0,0,0,0.06)] backdrop-blur-sm supports-[backdrop-filter]:bg-white/90"
                style={{
                  paddingBottom:
                    "max(0.35rem, env(safe-area-inset-bottom, 0px))",
                }}
                aria-label={t("work_order.view.mobileNav.ariaLabel")}
              >
                <div className="flex items-stretch justify-around gap-0 px-0.5 pt-1">
                  {qualityChecklistsModule &&
                  (work_order?.uses_checklist ||
                    work_order?.checklist_id) ? (
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/dashboard/work_orders/${work_order.id}/checklist`)
                      }
                      className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                    >
                      <ClipboardDocumentIcon
                        className="h-5 w-5 shrink-0"
                        aria-hidden
                      />
                      <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                        {t("work_orders.checklist.mobile_nav")}
                      </span>
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={!permissions.canItemConsumption}
                    onClick={() =>
                      navigate(
                        `/dashboard/work_orders/${work_order.id}/item-consumption`
                      )
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:pointer-events-none disabled:opacity-40"
                  >
                    <CubeIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.items")}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTimeRegistrationDialogSignal((n) => n + 1)
                    }
                    className="flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-0.5 py-1.5 text-gray-600 transition-colors hover:bg-gray-50 active:bg-gray-100"
                  >
                    <ClockIcon className="h-5 w-5 shrink-0" aria-hidden />
                    <span className="max-w-full text-center text-[10px] font-medium leading-tight">
                      {t("report.view.mobileNav.timeRegistration")}
                    </span>
                  </button>
                </div>
              </nav>
            )}
          </>
          {isMananger && (
            <PMDialog
              title="Godkjenn arbeidsordre"
              open={approveDialogOpen}
              onClose={() => setApproveDialogOpen(false)}
            >
              <Formik
                enableReinitialize={true} // Enable automatic reinitialization
                initialValues={{
                  id: work_order?.id,
                  is_approved: work_order?.is_approved,
                  is_rejected: work_order?.is_rejected,
                  approval_note: work_order?.approval_note,
                }} // Use the separate initialValues state
                validationSchema={Yup.object({
                  description: Yup.string(),
                })}
                onSubmit={(values, helpers) => {
                  axiosWithToast(
                    createOrUpdate(values),
                    (data) => {
                      // Extract the actual work order data from the response
                      const workOrderData = data?.data || data;
                      
                      if (workOrderData?.id) {
                        setWorkOrder(workOrderData); // Update work_order state
                        setInitialValues((prev) => ({
                          ...prev,
                          ...values,
                          id: workOrderData.id, // Update the ID with the actual ID from the response
                        }));
                        helpers.setStatus({ success: true });
                        helpers.setSubmitting(false);
                      } else {
                        console.error('No work order data received:', workOrderData);
                        helpers.setSubmitting(false);
                      }
                    },
                    () => {
                      helpers.setSubmitting(false); // Ensure submitting state is reset
                    },
                    () => {
                      setApproveDialogOpen(false);
                    },
                    "FravÃ¦r ble lagret",
                    (error) => {
                      setErrors(error); // Set errors for display
                      helpers.setErrors({
                        submit: "An error occurred while saving.",
                      }); // Update Formik errors
                      helpers.setSubmitting(false); // Reset submitting state
                    }
                  ).catch((err) => {
                    console.error("Submission error:", err);
                    helpers.setErrors({ submit: err.message });
                    helpers.setSubmitting(false);
                  });
                }}
              >
                {(formik) => (
                  <Form>
                    {errors &&
                      errors.length > 0 &&
                      errors.map((error, index) => (
                        <PMNotice
                          severity={"error"}
                          key={index}
                          title="Error!"
                          description={error}
                        />
                      ))}

                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
                      <div className="col-span-1 sm:col-span-1">
                        <PMSwitch
                          label="Godkjent"
                          name="is_approved"
                          {...formik}
                          onChange={(e) => {
                            formik.setFieldValue("is_rejected", false);
                            formik.handleChange(e);
                          }}
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-1">
                        <PMSwitch
                          label="Ikke godkjent"
                          name="is_rejected"
                          {...formik}
                          onChange={(e) => {
                            formik.setFieldValue("is_approved", false);
                            formik.handleChange(e);
                          }}
                        />
                      </div>

                      <div className="col-span-2 sm:col-span-2">
                        <PMField
                          label="Notat"
                          name="approval_note"
                          multiline={true}
                          rows={2}
                          {...formik}
                        />
                      </div>

                      <div className="col-span-2 sm:col-span-2 flex justify-between">
                        <PMButton
                          type="button"
                          onClick={() => setApproveDialogOpen(false)}
                          className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                        >
                          Avbryt
                        </PMButton>
                        <PMButton className="ml-10 max-w-1/2" type="submit">
                          Lagre
                        </PMButton>
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </PMDialog>
          )}

          {!permissions.isClosed && (
            <>


              {/* Item Consumption Dialog */}
              {permissions.canItemConsumption && (
                <PMDialog
                  size="4xl"
                  title="Vareforbruk"
                  open={itemConsumptionDialogOpen}
                  onClose={() => {
                    setItemConsumptionDialogOpen(false);
                    // Clear any errors when dialog is closed
                    setErrors([]);
                  }}
                >
                  <Formik
                    key={`formik-${work_order?.id}-${work_order?.updated_at}`} // Force reinitialization when work_order changes
                    enableReinitialize={true}
                    initialValues={{
                      id: work_order?.id,
                      order_items: work_order?.items || [],
                    }}
                    onSubmit={async (values, helpers) => {
                      try {
                        const data = await createOrUpdate(values);
                        
                        // Ensure we have the correct data structure
                        const workOrderData = data?.data || data;
                        
                        // Update only the items field to avoid triggering unnecessary effects
                        setWorkOrder(prev => ({
                          ...prev,
                          items: workOrderData?.items || workOrderData?.order_items || [],
                          updated_at: workOrderData?.updated_at || new Date().toISOString()
                        }));
                        
                        helpers.setStatus({ success: true });
                        helpers.setSubmitting(false);
                        // Small delay to ensure state is updated before closing dialog
                        setTimeout(() => {
                          setItemConsumptionDialogOpen(false);
                        }, 100);
                        toast.success("Vareforbruk lagret!");
                      } catch (error) {
                        console.error("Submission error:", error);
                        setErrors([error.message || "An error occurred while saving."]);
                        helpers.setErrors({
                          submit: "An error occurred while saving.",
                        });
                        helpers.setSubmitting(false);
                      }
                    }}
                  >
                    {(formik) => (
                      <Form>
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 gap-4">
                            <OrderItems
                              key={`order-items-${work_order?.id}-${work_order?.updated_at}`}
                              name="order_items"
                              group={false}
                              sales_price={false}
                              purchase_price={true}
                              discount={false}
                              surcharge={false}
                              drag={false}
                              quantity_picked={true}
                              quantity={true}
                              quote_line={false}
                              {...formik}
                            />
                          </div>

                          <div className="flex justify-end space-x-4 mt-4">
                            <PMButton
                              type="button"
                              onClick={() => {
                                setItemConsumptionDialogOpen(false);
                                setErrors([]); // Clear errors when canceling
                              }}
                              className="bg-slate-400 hover:bg-slate-200"
                            >
                              Avbryt
                            </PMButton>
                            <PMButton type="submit">Lagre</PMButton>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </PMDialog>
              )}
            </>
          )}
        </DashboardHeader>
      )}

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={t("work_orders.delete_confirm")}
        action="delete"
        module="work_order"
        yesCallback={handleDeleteWorkOrder}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />

      <WorkOrderPrintDialog
        open={workOrderPrintDialogOpen}
        onClose={() => setWorkOrderPrintDialogOpen(false)}
        workOrder={work_order}
        onRefresh={(data) => {
          if (data) {
            setWorkOrder(data);
          }
        }}
      />

      <WorkOrderFinishDialog
        open={workOrderFinishDialogOpen}
        onClose={() => setWorkOrderFinishDialogOpen(false)}
        selected={work_order}
        fields={initialValues}
        isLoading={isLoading}
        onRefresh={(data) => {
          if (!data) return;
          setWorkOrder((prev) =>
            prev?.id != null &&
            data.id != null &&
            String(prev.id) === String(data.id)
              ? { ...prev, ...data }
              : data
          );
        }}
      />

      <WorkOrderBillingReadyDialog
        open={billingReadyDialogOpen}
        onClose={() => setBillingReadyDialogOpen(false)}
        workOrder={work_order}
        hours={hoursState?.data || []}
        onSaved={(wo) => {
          if (wo) setWorkOrder(wo);
        }}
      />

      <WorkOrderAdminSetStatusDialog
        open={adminSetStatusDialogOpen}
        onClose={() => setAdminSetStatusDialogOpen(false)}
        workOrder={work_order}
        onSaved={(data) => {
          if (!data) return;
          setWorkOrder((prev) =>
            prev?.id != null &&
            data.id != null &&
            String(prev.id) === String(data.id)
              ? { ...prev, ...data }
              : prev
          );
        }}
      />
    </>
  );
};


